import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Badge } from "@/components/ui/badge";

/**
 * Workshop: Agent activity feed panel — list of agents and their actions.
 */

type AgentStatus = "active" | "paused" | "idle";

interface AgentActivity {
  id: string;
  name: string;
  color: string;
  status: AgentStatus;
  description: string;
}

interface AgentActivityPanelProps {
  activities?: AgentActivity[];
  onActivityClick?: (agentId: string) => void;
  className?: string;
  "data-testid"?: string;
}

const statusToBadgeVariant: Record<
  AgentStatus,
  "default" | "warning" | "outline"
> = {
  active: "default",
  paused: "warning",
  idle: "outline",
};

const statusLabel: Record<AgentStatus, string> = {
  active: "Active",
  paused: "Paused",
  idle: "Idle",
};

export function AgentActivityPanel({
  activities = [],
  onActivityClick,
  className,
  "data-testid": testId = "agent-activity-panel",
}: AgentActivityPanelProps) {
  return (
    <div
      className={cn(
        "flex w-80 flex-col rounded-xl border border-glass-stroke bg-glass-fill shadow-glass backdrop-blur-glass-md",
        className,
      )}
      data-testid={testId}
    >
      {activities.length === 0 && (
        <p className="px-4 py-6 text-center text-[10px] text-muted">
          No active agents
        </p>
      )}

      {activities.map((activity, index) => (
        <button
          key={activity.id}
          type="button"
          onClick={() => onActivityClick?.(activity.id)}
          className={cn(
            "flex flex-col gap-0.5 px-4 py-2 text-left transition-colors hover:bg-glass-fill-heavy",
            index < activities.length - 1 && "border-b border-border",
          )}
          data-testid={`${testId}-item-${activity.id}`}
        >
          <div className="flex w-full items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: activity.color }}
              aria-hidden="true"
            />
            <span className="flex-1 truncate text-xs font-medium text-secondary">
              {activity.name}
            </span>
            <Badge
              variant={statusToBadgeVariant[activity.status]}
              dot={false}
              data-testid={`${testId}-badge-${activity.id}`}
            >
              {statusLabel[activity.status]}
            </Badge>
            <Icons.ChevronDown className="h-2.5 w-2.5 text-muted" />
          </div>
          <span className="pl-4 text-[10px] text-muted">
            {activity.description}
          </span>
        </button>
      ))}
    </div>
  );
}

export type { AgentActivityPanelProps, AgentActivity, AgentStatus };
